import { PositionalAudio, useTexture } from '@react-three/drei';
import { useFrame, useThree, type ThreeElements } from '@react-three/fiber';
import { useControls } from 'leva';
import { memo, useLayoutEffect, useMemo, useRef } from 'react';
import {
  color,
  Fn,
  hash,
  instancedBufferAttribute,
  instanceIndex,
  time,
  uniform,
  vec3,
  mod,
  remapClamp,
  texture,
  uv,
  mix,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import useGameStore from '../../store/useGameStore';

const Flame = (props: ThreeElements['sprite']) => {
  const spriteMesh = useRef<THREE.Sprite>(null!);
  const materialRef = useRef<ThreeElements['spriteNodeMaterial']>(null!);

  const pointLightRef = useRef<THREE.PointLight>(null!);

  /**-----------------
        Controls
   -------------- */
  const controls = useControls(
    'flameControls',
    {
      uLife: {
        value: 0.32,
        min: 0,
        max: 4,
        step: 0.01,
      },
      uColorA: {
        value: '#ffbf00',
      },
      uColorB: {
        value: '#ff1108',
      },
      uTimeCoEfficent: {
        min: 0.31,
        max: 1,
        step: 0.001,
        value: 0.05,
      },
      radius: {
        value: 0.05,
        min: 0.01,
        max: 1,
        step: 0.01,
      },
      uSize: {
        value: 0.32,
        min: 0.001,
        max: 4,
        step: 0.01,
      },
      fireLightPosition: {
        value: [0, 0.2, 0],
      },
    },
    { collapsed: true }
  );

  const gl = useThree((state) => state.gl as unknown as THREE.WebGPURenderer);

  const [fireTexture] = useTexture(['/texture/fire_01.png']);

  //unforms
  const uLife = uniform(controls.uLife, 'float');
  const uColorA = uniform(color(controls.uColorA));
  const uColorB = uniform(color(controls.uColorB));
  const uTimeCoEfficent = uniform(controls.uTimeCoEfficent, 'float');
  const uSize = uniform(controls.uSize, 'float');

  const { count, radius } = useMemo(() => {
    const count = props.count ?? 100;
    const radius = controls.radius;
    return { count, radius };
  }, [props.count, controls.radius]);

  const instanceArray = useMemo(() => {
    const postionArray = new Float32Array(count * 3);
    const timeMul = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      //basic atrributes
      timeMul[i] = Math.random();

      const shape = new THREE.Cylindrical(
        radius,
        Math.random() * Math.PI * 2,
        0.01
      );

      const position = new THREE.Vector3();
      position.setFromCylindrical(shape);

      const i3 = i * 3;
      postionArray[i3] = position.x;
      postionArray[i3 + 1] = position.y;
      postionArray[i3 + 2] = position.z;
    }

    const postionBufferAttribute = new THREE.InstancedBufferAttribute(
      postionArray,
      3
    );
    const instancePositionAttribute = instancedBufferAttribute(
      postionBufferAttribute
    );

    return { instancePositionAttribute };
  }, [count, radius]);

  useLayoutEffect(() => {
    //life of particle
    const life = mod(time.mul(hash(instanceIndex)).mul(uTimeCoEfficent), uLife);

    const fireNode = Fn(() => {
      const instancePos = instanceArray.instancePositionAttribute;

      return instancePos.add(
        vec3(instancePos.x, instancePos.y.add(life), instancePos.z)
      );
    });

    const scaleNode = Fn(() => {
      const scalingProgress = remapClamp(life, 0.0, uLife, 1.0, 0.0);

      return uSize.mul(scalingProgress);
    });

    //fragment shader
    const alphatexture = texture(fireTexture, uv()).r;

    const fireColorProgress = remapClamp(life, 0.0, uLife, 1.0, 0.0);

    const colorData = mix(uColorA, uColorB, fireColorProgress).xyz;

    materialRef.current.positionNode = fireNode();
    materialRef.current.scaleNode = scaleNode();
    materialRef.current.colorNode = vec4(colorData, alphatexture);

    materialRef.current.needsUpdate = true;

    // return ()=> {
    //   pane.dispose()
    // }
  }, [
    gl,
    instanceArray.instancePositionAttribute,
    uColorA,
    uColorB,
    fireTexture,
    uLife,
    count,
    uSize,
    uTimeCoEfficent,
  ]);

  useFrame((state) => {
    if (!pointLightRef.current) return;
    const t = state.clock.getElapsedTime();

    // slightly faster sine + small random jitter
    const jitter = Math.sin(t * 10) * Math.random() + 2.0 + Math.random();
    pointLightRef.current.intensity = jitter;
  });

  const playAudio = useGameStore((state) => state.playAudio);

  return (
    <group>
      <sprite ref={spriteMesh} count={count} {...props}>
        {/* <bufferGeometry ref={geometryRef} /> */}
        <spriteNodeMaterial
          ref={materialRef}
          side={THREE.DoubleSide}
          transparent={true}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </sprite>
      <pointLight
        args={[controls.uColorA, 10, 50, 3]}
        position={controls.fireLightPosition}
        ref={pointLightRef}
        color={controls.uColorA}
      />
      {playAudio && (
        <PositionalAudio
          autoplay
          loop
          url='/audio/fireplace.mp3'
          distance={1.5}
        />
      )}
    </group>
  );
};

export default memo(Flame);
